import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Divider, ListSubheader, Menu, MenuItem, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useDispatch } from 'react-redux';

import { useAuthContext } from '../../context/auth-context';
import { getViewAs, setViewAs, VIEW_AS_EVENT } from '../../lib/view-as';
import { ROLES as ROLE_VALUES, roleLabel } from '../../constants/roles';
import { api } from '../../redux/services/api';
import { useGetAllAffiliationsQuery } from '../../redux/services/affiliation-api';

/**
 * Roles previsualizables. `super-admin` no está: nunca se puede escalar hacia él.
 *
 * Las etiquetas salen de `constants/roles.js`, que es donde vive el vocabulario de
 * roles de la interfaz. Aquí había una segunda copia de la tabla, así que renombrar
 * un rol dejaba los dos sitios diciendo cosas distintas.
 */
const ROLES = [ROLE_VALUES.ADMIN, ROLE_VALUES.SEC, ROLE_VALUES.BASIC].map(value => ({
  value,
  label: roleLabel(value),
}));

/**
 * "Ver como": permite a un administrador mirar la aplicación con los permisos de
 * otro rol y, para el Secretario de Zona, con una zona concreta.
 *
 * No es un adorno de interfaz: las peticiones viajan con las cabeceras
 * `X-View-As-*` y el servidor degrada la identidad de verdad, así que lo que se ve
 * es lo que vería esa persona. Por eso la vista previa es de **solo lectura** — el
 * backend rechaza cualquier escritura mientras está activa.
 *
 * El control está partido en dos a propósito, porque sus dos mitades tienen
 * necesidades opuestas:
 *
 * - La ENTRADA (`useViewAs` + `ViewAsMenu`) puede vivir escondida: la pinta el
 *   sidebar, junto al perfil, que es donde está el resto de la identidad.
 * - El AVISO (`ViewAsBanner`) no puede desaparecer nunca. Va en el flujo del
 *   contenido, no flotando: antes era `position: fixed` y tapaba los botones de
 *   acción de cada página. Y no puede vivir en el sidebar porque en móvil es un
 *   drawer: con el drawer cerrado nada indicaría que la vista previa sigue activa.
 */
export function useViewAs(menuAbierto = false) {
  const { hasRealPermission, activeCamp, refreshIdentity } = useAuthContext();
  const dispatch = useDispatch();
  const [current, setCurrent] = useState(getViewAs);

  // Solo las zonas del campamento activo: con `campId`, el endpoint devuelve las
  // afiliaciones hijas de la afiliación de ese camp. Ofrecer zonas de otro camp no
  // serviría de nada — el reporte saldría vacío y parecería un fallo.
  // Se piden solo al abrir el menú.
  const { data: affiliations, isFetching } = useGetAllAffiliationsQuery(
    { campId: activeCamp?._id },
    { skip: !menuAbierto || !activeCamp?._id }
  );

  // Hay dos instancias montadas (la entrada y el aviso) y el evento las mantiene
  // sincronizadas: al aplicar en una, la otra se entera.
  useEffect(() => {
    const onChange = e => setCurrent(e.detail);
    window.addEventListener(VIEW_AS_EVENT, onChange);
    return () => window.removeEventListener(VIEW_AS_EVENT, onChange);
  }, []);

  /** Zonas del campamento activo. Las afiliaciones raíz no son zonas reales. */
  const zones = useMemo(() => {
    const list = (affiliations || []).filter(a => a.parent && a.zone).map(a => a.zone);
    return [...new Set(list)].sort((a, b) => a.localeCompare(b, 'es', { numeric: true }));
  }, [affiliations]);

  const apply = useCallback(
    next => {
      // El orden importa: primero las cabeceras, para que todo lo que salga después
      // viaje ya con la identidad nueva.
      setViewAs(next);
      // Sin esto la interfaz seguiría mostrando lo que ya tenía cacheado con la
      // identidad anterior, que es justo lo que la vista previa quiere evitar.
      dispatch(api.util.resetApiState());
      // Y sin esto la vista previa no previsualizaba nada: `permissions` solo se
      // refrescaba al montar, así que el menú, las rutas y los botones de escritura
      // seguían siendo los del administrador mientras el aviso decía "viendo como
      // Juez". Solo se notaba al recargar a mano.
      refreshIdentity();
    },
    [dispatch, refreshIdentity]
  );

  return {
    // Se mira el permiso REAL, no el de la vista previa: si no, previsualizar un rol
    // bajo escondería este mismo control y no habría manera de salir.
    puedeVerComo: hasRealPermission('users.change-role'),
    current,
    apply,
    zones,
    isFetching,
    activeCamp,
  };
}

/** Etiqueta legible de una vista previa activa. */
export const viewAsLabel = current =>
  current ? `${roleLabel(current.role)}${current.zone ? ` · ${current.zone}` : ''}` : '';

/** El menú de selección. Lo comparten la entrada del sidebar y el aviso. */
export function ViewAsMenu({ anchor, onClose, current, apply, zones, isFetching, activeCamp }) {
  const elegir = next => {
    apply(next);
    onClose();
  };

  return (
    <Menu
      anchorEl={anchor}
      open={Boolean(anchor)}
      onClose={onClose}
      slotProps={{ paper: { sx: { maxHeight: 420, width: 280 } } }}
    >
      <ListSubheader sx={{ lineHeight: '32px' }}>Ver la aplicación como…</ListSubheader>

      {ROLES.filter(r => r.value !== ROLE_VALUES.SEC).map(r => (
        <MenuItem key={r.value} selected={current?.role === r.value} onClick={() => elegir({ role: r.value, zone: null })}>
          {r.label}
        </MenuItem>
      ))}

      <Divider />
      <ListSubheader sx={{ lineHeight: '32px' }}>
        Secretario de Zona
        {activeCamp?.name ? ` · ${activeCamp.name}` : ''}
      </ListSubheader>

      {!activeCamp?._id && (
        <MenuItem disabled>
          <Typography variant="caption">Selecciona un campamento primero</Typography>
        </MenuItem>
      )}
      {activeCamp?._id && isFetching && (
        <MenuItem disabled>
          <Typography variant="caption">Cargando zonas…</Typography>
        </MenuItem>
      )}
      {activeCamp?._id && !isFetching && zones.length === 0 && (
        <MenuItem disabled>
          <Typography variant="caption">Este campamento no tiene zonas asociadas</Typography>
        </MenuItem>
      )}
      {zones.map(zone => (
        <MenuItem
          key={zone}
          selected={current?.role === 'sec' && current?.zone === zone}
          onClick={() => elegir({ role: 'sec', zone })}
        >
          {zone}
        </MenuItem>
      ))}

      {current && [
        <Divider key="div" />,
        <MenuItem key="salir" onClick={() => elegir(null)}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Salir de la vista previa
          </Typography>
        </MenuItem>,
      ]}
    </Menu>
  );
}

/**
 * Franja de aviso mientras la vista previa está activa.
 *
 * Va DENTRO del flujo del contenido (la pinta `App.js` sobre las rutas): empuja el
 * contenido en vez de taparlo. Mientras no haya vista previa no ocupa nada.
 */
export function ViewAsBanner() {
  const [anchor, setAnchor] = useState(null);
  const estado = useViewAs(Boolean(anchor));
  const { puedeVerComo, current, apply } = estado;

  if (!puedeVerComo || !current) return null;

  return (
    <>
      {/* Va en flujo puro, sin `sticky`. Se intentó fijarlo bajo el AppBar y sale caro:
          `main` lleva `overflow-x: hidden`, así que cuenta como contenedor de scroll y
          el `top` se suma a la posición de flujo — 56px de hueco muerto en cada pantalla
          móvil aunque no se haya hecho scroll. El indicador permanente es el ítem "Ver
          como" del sidebar, que queda marcado como activo. */}
      <Box sx={{ mb: 2 }}>
        <Alert
          severity="warning"
          variant="filled"
          icon={<VisibilityIcon fontSize="inherit" />}
          action={
            <>
              {/* En móvil sobra: el menú también se abre desde el sidebar, y aquí el
                  ancho es justo para el texto y la salida. */}
              <Button
                color="inherit"
                size="small"
                onClick={e => setAnchor(e.currentTarget)}
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              >
                Cambiar
              </Button>
              <Button color="inherit" size="small" variant="outlined" onClick={() => apply(null)}>
                Salir
              </Button>
            </>
          }
          sx={{ alignItems: 'center', boxShadow: 2, py: 0.5 }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Viendo como {viewAsLabel(current)}
          </Typography>
          <Typography variant="caption" sx={{ display: { xs: 'none', md: 'block' } }}>
            Solo lectura: el servidor rechaza cualquier cambio mientras dure la vista previa.
          </Typography>
        </Alert>
      </Box>

      <ViewAsMenu anchor={anchor} onClose={() => setAnchor(null)} {...estado} />
    </>
  );
}

export default ViewAsBanner;
